import { useCallback, useEffect, useState } from 'react'

/**
 * Harita zemininin görünüm modu.
 *
 * - `satellite` — tile'lar ham renkleriyle; arazi dokusunu (ağaç hattı, bina,
 *   yol) okumak için.
 * - `tactical`  — gri tonlamaya indirgenmiş, parlaklığı normalize edilmiş
 *   zemin; harita bir *resim* değil, üstüne bilgi konacak bir tuval olur.
 */
export type MapViewMode = 'satellite' | 'tactical'

/**
 * Uydu modunun yumuşatması.
 *
 * Tile'lar ham hâlleriyle **çok doygun**: yeşil tarla, turuncu toprak ve mavi
 * su, üstüne konan her şeyle (ok, çember, marker, etiket) renk yarışına
 * giriyor ve harita uzun bakışta yoruyor. Filtre haritayı gri yapmaz —
 * arazi dokusu okunmaya devam eder — yalnızca doygunluğu yarıya indirir,
 * biraz karartır ve kaybolan ayrımı kontrastla geri verir.
 *
 * Değerler `wardogs.tech`'in savaş odasından alındı (`--map-filter`); referans
 * olarak gösterilen görünüm birebir odur.
 */
const SATELLITE_FILTER = 'saturate(0.45) contrast(1.08) brightness(0.82)'

/** Harita görüntüsünün gri tonlamadaki parlaklık istatistikleri (0..1). */
export interface LumaStats { mean: number; sd: number }

/**
 * Taktiksel görünümün hedeflediği gri tonlama istatistiği: ortalama luma ve
 * standart sapma. Her harita bu değerlere indirgenir.
 *
 * Amaç haritaları "koyulaştırmak" değil, **eşitlemek**. Zestafona'nın çölü ile
 * Ozeti'nin yeşilliği aynı ortalamaya ve aynı kontrasta oturunca marker
 * renkleri, ok'lar ve etiketler üç haritada da birebir aynı okunurlukta çıkar —
 * zemin haritadan haritaya değişen bir değişken olmaktan çıkar.
 */
const TARGET_MEAN = 0.238
const TARGET_SD   = 0.1007

/**
 * Taktiksel görünümün gücü (0..1). 1 = tam normalizasyon.
 *
 * Bizim editörde harita çoğu zaman *okunacak arazi* olarak da kullanılıyor, o
 * yüzden burayı düşürmek zemini biraz daha okunur bırakır; 0 tamamen ham
 * görüntü demektir (yani `satellite` ile aynı).
 */
const TACTICAL_STRENGTH = 1

/**
 * Verilen harita istatistiğinden CSS filtresini çözer.
 *
 * Filtre `saturate() brightness() contrast()` sırasıyla uygulanır, yani bir
 * piksel için: `v → v·b → (v·b − 0.5)·c + 0.5`. Sonuçtaki ortalama ve standart
 * sapma:
 *
 *     μ' = (μ·b − 0.5)·c + 0.5
 *     σ' = σ·b·c
 *
 * `μ' = TARGET_MEAN`, `σ' = TARGET_SD` denklemlerini `b` ve `c` için çözünce
 * aşağıdaki kapalı form çıkar — her harita için tek seferde hesaplanır, iterasyon
 * yok.
 *
 * `strength` ile kimlik değerlerine (1, 1, 1) doğru karıştırılır, böylece mod
 * bir aç/kapa olmaktan çıkıp ayarlanabilir hale gelir.
 */
export function tacticalFilter(luma: LumaStats, strength = TACTICAL_STRENGTH): string {
  const r = Math.min(1, Math.max(0, strength))
  const contrast   = 2 * (TARGET_SD * luma.mean / luma.sd + 0.5 - TARGET_MEAN)
  const brightness = TARGET_SD / (luma.sd * contrast)
  return [
    `saturate(${(1 - r).toFixed(3)})`,
    `brightness(${(1 + (brightness - 1) * r).toFixed(3)})`,
    `contrast(${(1 + (contrast - 1) * r).toFixed(3)})`,
  ].join(' ')
}

/**
 * Moda karşılık gelen CSS filtresi.
 *
 * Uydu modu artık **filtresiz değil**: ham tile'lar aşırı doygun geliyor ve
 * zemin üstündeki çizimle yarışıyor (bkz. `SATELLITE_FILTER`). Taktiksel mod
 * ise eskisi gibi haritaları ortak bir griye oturtur.
 *
 * Harita istatistiği bilinmiyorsa (katalogda `luma` yoksa) taktiksel mod
 * **uydu yumuşatmasına** düşer: uydurma bir sabitle yanlış normalize etmek
 * yerine en azından aynı sakin zemin kalır.
 */
export function mapFilter(luma: LumaStats | undefined, mode: MapViewMode): string | undefined {
  if (mode !== 'tactical' || !luma) return SATELLITE_FILTER
  return tacticalFilter(luma)
}

const STORAGE_KEY = 'tll_map_view'

/**
 * Görünüm modu + kalıcılık. Tercih kullanıcı başına saklanır; varsayılan
 * `satellite`, yani mevcut davranış.
 */
export function useMapViewMode(): [MapViewMode, (m: MapViewMode) => void, () => void] {
  const [mode, setModeState] = useState<MapViewMode>(() => {
    if (typeof window === 'undefined') return 'satellite'
    return localStorage.getItem(STORAGE_KEY) === 'tactical' ? 'tactical' : 'satellite'
  })

  // Aynı sekmedeki diğer editör örneği de değişikliği görsün.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setModeState(e.newValue === 'tactical' ? 'tactical' : 'satellite')
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const setMode = useCallback((m: MapViewMode) => {
    setModeState(m)
    try { localStorage.setItem(STORAGE_KEY, m) } catch { /* private mode */ }
  }, [])

  const toggle = useCallback(
    () => setMode(mode === 'tactical' ? 'satellite' : 'tactical'),
    [mode, setMode],
  )

  return [mode, setMode, toggle]
}
